import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-lg border bg-card text-card-foreground shadow-sm transition-all duration-200",
  {
    variants: {
      variant: {
        default: "border-border bg-card shadow-md",
        outline: "border-2 border-border bg-background",
        elevated: "border-border bg-card shadow-lg",
        
        // Memara-specific variants
        sanctuary: 
          "border-workspace-border bg-gradient-to-br from-workspace-surface-50 to-workspace-surface-100 shadow-workspace-soft",
        memory: 
          "border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 shadow-sm hover:shadow-md",
        professional: 
          "border-border bg-card shadow-sm hover:shadow-md dark:border-slate-700 dark:bg-slate-900",
        integration: 
          "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm",
        
        // Realm-specific variants
        "outer-realm": 
          "border-outer-surface-200 bg-gradient-to-br from-outer-surface-50 to-outer-surface-100 font-outer",
        "memory-workspace": 
          "border-workspace-border bg-workspace-surface-primary text-workspace-text-primary font-workspace",
        
        // Interactive variants
        interactive: 
          "border-border bg-card hover:bg-accent/5 cursor-pointer transition-colors",
        "interactive-workspace": 
          "border-workspace-border bg-gradient-to-br from-workspace-surface-50 to-workspace-surface-100 hover:from-workspace-primary-50 hover:to-workspace-primary-100 cursor-pointer shadow-workspace-soft hover:shadow-workspace-medium",
        
        // Status variants
        success: "border-emerald-200 bg-emerald-50 text-emerald-900",
        warning: "border-amber-200 bg-amber-50 text-amber-900",
        error: "border-red-200 bg-red-50 text-red-900",
        info: "border-blue-200 bg-blue-50 text-blue-900",
        
        // Enhanced contrast variants
        "primary-accent": "border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-md hover:shadow-lg",
        "brand-primary": "border-[#007AFF]/30 bg-gradient-to-br from-[#007AFF]/5 to-[#007AFF]/10 shadow-md",
        "brand-purple": "border-[#5856D6]/30 bg-gradient-to-br from-[#5856D6]/5 to-[#5856D6]/10 shadow-md",
        "enhanced-white": "border-border bg-card shadow-md hover:shadow-lg hover:border-muted-foreground/30",
      },
      size: {
        sm: "p-3",
        default: "p-6",
        lg: "p-8",
        xl: "p-10",
      },
      rounded: {
        none: "rounded-none",
        sm: "rounded-sm",
        default: "rounded-lg",
        lg: "rounded-xl",
        xl: "rounded-2xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
    },
  }
)

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>
>(({ className, variant, size, rounded, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(cardVariants({ variant, size, rounded, className }))}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  }
>(({ className, as: Comp = "h3", ...props }, ref) => (
  <Comp
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { 
  Card, 
  CardHeader, 
  CardFooter, 
  CardTitle, 
  CardDescription, 
  CardContent,
  cardVariants 
}
